import { useEffect, useState } from "react";

import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";

export interface CallingHoursStatus {
  allowed: boolean;
  reason?: string;
  localTime?: string;
  timezone?: string;
  exempt?: boolean;
}

/**
 * Per-lead TCPA calling-hours status, used to LOCK call/text buttons when a lead
 * is outside 8am–9pm in their local time (exempt: Underwriting, Sold & Approved,
 * appointment). Re-checks every 5 min so the lock flips as 9pm rolls across
 * timezones.
 *
 * Fails OPEN: if the check errors, `status` stays null and the UI does NOT lock —
 * the backend is the real hard stop, this is just convenience/visibility.
 */
export function useLeadCallingHours(leadId?: string | null): {
  status: CallingHoursStatus | null;
  loading: boolean;
} {
  const [status, setStatus] = useState<CallingHoursStatus | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!leadId) {
      setStatus(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const run = async () => {
      try {
        const res: any = await api.get({
          url: getApiUrl(`/communication/calling-hours/lead/${leadId}`),
          auth: true,
        });
        const data = (res?.data ?? res) as CallingHoursStatus;
        if (!cancelled) setStatus(data || null);
      } catch {
        if (!cancelled) setStatus(null); // fail open
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    const interval = setInterval(run, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [leadId]);

  return { status, loading };
}
